import { useEffect, useState } from "react";
import type { ConnectorDTO, SubjectDTO, SubjectTypeDTO } from "../../shared/types";
import { get, post } from "../lib/api";
import { navigate } from "../lib/router";
import { scopeName, scopePath, shortId, toolName } from "../lib/scopes";
import { EmptyRow, List, listRowCls, ListRow } from "./List";
import { Dialog } from "./Dialog";
import { ToolMark } from "./ScopeTypeForm";
import { ShowArchived, useShowArchived } from "./ShowArchived";
import { Badge, Button, ErrorText, Field, inputCls } from "./ui";

const PAGE = 50;

/** A new scope of one type: its name, and its id in each of the type's tools (any of them may wait). */
function NewScopeForm({ type, connectors, onCreated }: { type: SubjectTypeDTO; connectors: ConnectorDTO[]; onCreated: (scope: SubjectDTO) => void }) {
  const [name, setName] = useState("");
  const [ids, setIds] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const links = Object.fromEntries(Object.entries(ids).map(([system, id]) => [system, id.trim()]).filter(([, id]) => id));
  return (
    <div className="space-y-4">
      <Field label="Name" hint="Shown everywhere; it can change any time. The scope's own id never does.">
        <input className={inputCls} value={name} placeholder="iSTORM" onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
      {type.fields.map((f, i) => (
        <Field key={f.system} label={`${toolName(connectors, f.system)} ${f.idField}`} hint={i === 0 ? "The main tool. Optional, like every id: add it later on the scope's page." : undefined}>
          <input className={`${inputCls} font-mono`} value={ids[f.system] ?? ""} onChange={(e) => setIds({ ...ids, [f.system]: e.target.value })} />
        </Field>
      ))}
      <ErrorText error={error} />
      <div className="flex justify-end">
        <Button
          variant="primary"
          disabled={busy || !name.trim()}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              onCreated(await post<SubjectDTO>("/api/subjects", { kind: type.key, name: name.trim(), links }));
            } catch (err) {
              setError((err as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Creating…" : "Create scope"}
        </Button>
      </div>
    </div>
  );
}

/** One type's scopes, by name, 50 at a time, searchable by name and by any tool's id; the archived ones only when asked for. */
export function ScopeList({ type, connectors }: { type: SubjectTypeDTO; connectors: ConnectorDTO[] }) {
  const [showArchived, setShowArchived] = useShowArchived();
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<{ items: SubjectDTO[]; total: number } | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    const query = new URLSearchParams({ kind: type.key, paged: "1", limit: String(PAGE), offset: String(offset), ...(q.trim() ? { q: q.trim() } : {}), ...(showArchived ? { archived: "all" } : {}) });
    // Typing waits a moment so every keystroke is not a request.
    const timer = setTimeout(() => {
      get<{ items: SubjectDTO[]; total: number }>(`/api/subjects?${query}`).then(
        (p) => {
          if (!current) return;
          setPage(p);
          setError(null);
        },
        (e) => current && setError(e.message),
      );
    }, q ? 200 : 0);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [type.key, type.archivedAt, q, offset, showArchived]);

  const total = page?.total ?? 0;
  const noun = type.name.toLowerCase();
  return (
    <>
      <List
        title={page ? `Scopes · ${total}` : "Scopes"}
        hint={`Each is one ${noun}, keyed ${type.key}:<id>. Open one for its ids, memory, skills and files.`}
        action={<Button variant="primary" onClick={() => setCreating(true)}>New scope</Button>}
        footer={
          total > PAGE && (
            <div className="flex items-center justify-between gap-3 text-xs text-muted">
              <span>
                {offset + 1}–{Math.min(offset + PAGE, total)} of {total}
              </span>
              <div className="flex gap-2">
                <Button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</Button>
                <Button disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>Next</Button>
              </div>
            </div>
          )
        }
        filters={
          <div className="space-y-2">
            {/* The switch only once there is something archived to show. */}
            {(type.archivedCount > 0 || showArchived) && (
              <ShowArchived
                on={showArchived}
                count={type.archivedCount}
                onChange={(on) => {
                  setShowArchived(on);
                  setOffset(0);
                }}
              />
            )}
            <input
              className={inputCls}
              value={q}
              placeholder="Search by name or by any tool's id"
              onChange={(e) => {
                setQ(e.target.value);
                setOffset(0);
              }}
              aria-label="Search scopes"
            />
          </div>
        }
      >
        {error && (
          <div className={listRowCls}>
            <ErrorText error={error} />
          </div>
        )}
        {page?.items.map((s) => {
          const ids = type.fields.filter((f) => s.links[f.system]);
          return (
            <ListRow
              key={s.id}
              to={scopePath(s)}
              title={
                <>
                  <span className="truncate text-sm font-medium">{scopeName(s)}</span>
                  {s.archivedAt && <Badge tone="amber">Archived</Badge>}
                </>
              }
              trailing={
                <span className={s.scopeCount ? "text-muted" : "text-faint"} title="Agents working under it">
                  {s.scopeCount === 0 ? "No agent yet" : `${s.scopeCount} ${s.scopeCount === 1 ? "agent" : "agents"}`}
                </span>
              }
            >
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
                <span className="font-mono" title={s.key}>{shortId(s.slug)}</span>
                {ids.map((f) => (
                  <span key={f.system} className="inline-flex min-w-0 items-center gap-1" title={`${toolName(connectors, f.system)} ${f.idField}: ${s.links[f.system]}`}>
                    <ToolMark system={f.system} connectors={connectors} className="size-3.5" />
                    <span className="max-w-[12rem] truncate font-mono">{shortId(s.links[f.system]!)}</span>
                  </span>
                ))}
                {ids.length === 0 && <span className="text-faint">no ids yet</span>}
              </div>
            </ListRow>
          );
        })}
        {page && page.items.length === 0 && (
          <EmptyRow>
            {q.trim() ? `No ${noun} matches "${q.trim()}".` : type.archivedCount && !showArchived ? `Every ${noun} scope is archived.` : `No ${noun} scopes yet. Create one here, or an agent with access creates its own.`}
          </EmptyRow>
        )}
        {!page && !error && (
          <div className={listRowCls}>
            <div className="h-24 animate-pulse rounded-xl bg-fill" />
          </div>
        )}
      </List>
      <Dialog open={creating} onClose={() => setCreating(false)} size="lg" title={`New ${noun} scope`}>
        <NewScopeForm type={type} connectors={connectors} onCreated={(scope) => navigate(scopePath(scope))} />
      </Dialog>
    </>
  );
}
